import type { ReactNode } from "react";
import { Pagination } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { DataTableLabels } from "./tableLabels";
import type { DataTablePaginationProps } from "./tableTypes";

interface DataTablePaginationBarProps extends DataTablePaginationProps {
  labels: DataTableLabels;
  placement: "top" | "bottom";
  /** Rendered before the page summary (e.g. the table title). */
  leading?: ReactNode;
  trailing?: ReactNode;
}

export function DataTablePagination({
  labels,
  placement,
  start,
  end,
  total,
  currentPage,
  totalPages,
  onPageChange,
  isLoading,
  leading,
  trailing,
}: DataTablePaginationBarProps) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-y-0 bg-surface-0",
        placement === "top" && "border-b border-surface-200",
        placement === "bottom" && "border-t border-surface-200",
      )}
    >
      {leading ? (
        <div className="flex min-w-0 items-center py-2.5 pl-4">{leading}</div>
      ) : null}
      <Pagination
        labels={labels}
        placement={placement}
        className={cn(
          "min-w-0 flex-1 rounded-none border-0 shadow-none",
          trailing && "sm:pr-2",
        )}
        page={currentPage}
        totalPages={totalPages}
        start={start}
        end={end}
        total={total}
        onPageChange={onPageChange}
        isLoading={isLoading}
        summary={labels.paginationSummary(start, end, total)}
      />
      {trailing ? (
        <div className="flex shrink-0 items-center gap-2 px-4 py-2 sm:py-0">
          {trailing}
        </div>
      ) : null}
    </div>
  );
}
